import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service } from '../service';
import { SetBotActiveIO } from '../types';

export const getSetBotActiveMutationKey = () => ['V4', 'SetBotActive'] as const;

type MutationContext<C = unknown> = {
  otherContext?: C;
};

type UseSetBotActiveMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetBotActiveIO['input'] = SetBotActiveIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetBotActiveIO, E, C, never, RequestOverride>;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `SetBotActive` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useSetBotActiveMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends SetBotActiveIO['input'] = SetBotActiveIO['input']
>({ options, httpOptions }: UseSetBotActiveMutationArgs<E, C | undefined, RequestOverride> = {}) =>
  useMutation<SetBotActiveIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getSetBotActiveMutationKey(),
    mutationFn: (req) => SchemaSMSDataV4Service.SetBotActive(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      // TODO: update different outcomes for all below
      return {
        // Add custom context type here (if exists in `MutationContext`)
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: (response, request, context) => {
      // Implement query updates or invalidation for affected queries here

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      // Revert optimistic update here (if implemented)

      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      // Only pass context of type C into provided `onSettled` option
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
